import React, { useState, useEffect, useRef } from 'react';
import {
  TaskWithDetails,
  Room,
  Trade,
  updateTask,
  uploadTaskPhoto,
  deleteTaskPhoto,
  getTaskPhotoUrl
} from '../api/client';

interface EditTaskModalProps {
  task: TaskWithDetails | null;
  rooms: Room[];
  trades: Trade[];
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  token: string;
}

export function EditTaskModal({
  task,
  rooms,
  trades,
  isOpen,
  onClose,
  onSave,
  onDelete,
  token
}: EditTaskModalProps) {
  const [editedTask, setEditedTask] = useState<Partial<TaskWithDetails>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roomSearch, setRoomSearch] = useState('');
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const roomInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (task && isOpen) {
      setEditedTask({ ...task });
      const room = rooms.find(r => r.id === task.room_id);
      setRoomSearch(room ? formatRoomDisplay(room) : '');
      setError(null);
      setShowRoomDropdown(false);
    }
  }, [task, isOpen, rooms]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (roomInputRef.current && !roomInputRef.current.contains(e.target as Node)) {
        setShowRoomDropdown(false);
      }
    };

    if (showRoomDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRoomDropdown]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving && !isDeleting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isSaving, isDeleting, onClose]);

  if (!isOpen || !task) return null;

  const formatRoomDisplay = (room: Room) => {
    return room.name ? `Room ${room.number} - ${room.name}` : `Room ${room.number}`;
  };

  const filteredRooms = rooms.filter(room => {
    const searchLower = roomSearch.toLowerCase();
    return (
      room.number.toLowerCase().includes(searchLower) ||
      (room.name && room.name.toLowerCase().includes(searchLower))
    );
  });

  const currentRoom = rooms.find(r => r.id === editedTask.room_id);
  const photoUrl = getTaskPhotoUrl(editedTask.photo_url || null);

  const hasChanges = () => {
    if (!task || !editedTask) return false;
    return (
      editedTask.room_id !== task.room_id ||
      editedTask.description !== task.description ||
      editedTask.trade_id !== task.trade_id ||
      editedTask.status !== task.status ||
      editedTask.due_date !== task.due_date
    );
  };

  const handleRoomSelect = (room: Room) => {
    setEditedTask(prev => ({ ...prev, room_id: room.id }));
    setRoomSearch(formatRoomDisplay(room));
    setShowRoomDropdown(false);
  };

  const handleSave = async () => {
    if (!editedTask.description?.trim()) {
      setError('Description is required');
      return;
    }

    if (!editedTask.room_id) {
      setError('Room is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updates: {
        description?: string;
        trade_id?: number | null;
        room_id?: number;
        status?: string;
        due_date?: string | null;
      } = {};

      if (editedTask.description !== task.description) {
        updates.description = editedTask.description;
      }
      if (editedTask.trade_id !== task.trade_id) {
        updates.trade_id = editedTask.trade_id;
      }
      if (editedTask.room_id !== task.room_id) {
        updates.room_id = editedTask.room_id;
      }
      if (editedTask.status !== task.status) {
        updates.status = editedTask.status;
      }
      if (editedTask.due_date !== task.due_date) {
        updates.due_date = editedTask.due_date;
      }

      if (Object.keys(updates).length > 0) {
        await updateTask(token, task.id, updates);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const { deleteTask } = await import('../api/client');
      await deleteTask(token, task.id);
      onDelete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
      setIsDeleting(false);
    }
  };

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    const fileExtension = file.name.toLowerCase();
    
    if (!validTypes.includes(file.type) && !fileExtension.endsWith('.heic')) {
      setError('Please select a valid image file (JPG, PNG, WebP, or HEIC)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Photo must be less than 10MB');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);

    try {
      const result = await uploadTaskPhoto(task.id, file);
      setEditedTask(prev => ({ ...prev, photo_url: result.photo_url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setIsDeletingPhoto(true);
    setError(null);

    try {
      await deleteTaskPhoto(token, task.id);
      setEditedTask(prev => ({ ...prev, photo_url: null }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving && !isDeleting) {
      onClose();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#dc3545';
      case 'done': return '#ffc107';
      case 'verified': return '#28a745';
      default: return '#666';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}
    >
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Edit Task</h2>
          <button
            onClick={onClose}
            disabled={isSaving || isDeleting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0 4px'
            }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {error && (
            <div style={{
              background: '#fee',
              color: '#c00',
              padding: '10px 12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Room */}
          <div style={{ marginBottom: '16px' }} ref={roomInputRef}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Room *
            </label>
            <input
              type="text"
              value={roomSearch}
              onChange={(e) => {
                setRoomSearch(e.target.value);
                setShowRoomDropdown(true);
              }}
              onFocus={() => setShowRoomDropdown(true)}
              placeholder="Search rooms..."
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            {showRoomDropdown && filteredRooms.length > 0 && (
              <div style={{
                position: 'absolute',
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflow: 'auto',
                zIndex: 1001,
                marginTop: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                width: 'calc(100% - 40px)'
              }}>
                {filteredRooms.map(room => (
                  <div
                    key={room.id}
                    onClick={() => handleRoomSelect(room)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fff';
                    }}
                  >
                    {formatRoomDisplay(room)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Description *
            </label>
            <textarea
              value={editedTask.description || ''}
              onChange={(e) => setEditedTask(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Trade */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Assigned To
            </label>
            <select
              value={editedTask.trade_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                setEditedTask(prev => ({
                  ...prev,
                  trade_id: value ? parseInt(value) : null
                }));
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
                background: '#fff'
              }}
            >
              <option value="">(No Trade)</option>
              {trades.map(trade => (
                <option key={trade.id} value={trade.id}>
                  {trade.name}
                </option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Due Date
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="date"
                value={formatDate(editedTask.due_date || null)}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditedTask(prev => ({
                    ...prev,
                    due_date: value ? value : null
                  }));
                }}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              {editedTask.due_date && (
                <button
                  onClick={() => setEditedTask(prev => ({ ...prev, due_date: null }))}
                  style={{
                    padding: '10px 16px',
                    background: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Status */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Status
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: getStatusColor(editedTask.status || 'open')
                }}
              />
              <select
                value={editedTask.status || 'open'}
                onChange={(e) => setEditedTask(prev => ({ ...prev, status: e.target.value as 'open' | 'done' | 'verified' }))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  background: '#fff'
                }}
              >
                <option value="open">Open</option>
                <option value="done">Done</option>
                <option value="verified">Verified</option>
              </select>
            </div>
          </div>

          {/* Photo */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Photo
            </label>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,.heic"
              onChange={handleUploadPhoto}
              style={{ display: 'none' }}
            />

            {photoUrl ? (
              <div>
                <img
                  src={photoUrl}
                  alt="Task photo"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    display: 'block'
                  }}
                  onClick={() => window.open(photoUrl, '_blank')}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingPhoto}
                    style={{
                      padding: '8px 16px',
                      background: '#f0f0f0',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: isUploadingPhoto ? 'wait' : 'pointer',
                      fontSize: '13px',
                      opacity: isUploadingPhoto ? 0.6 : 1
                    }}
                  >
                    {isUploadingPhoto ? 'Uploading...' : 'Replace Photo'}
                  </button>
                  <button
                    onClick={handleDeletePhoto}
                    disabled={isDeletingPhoto}
                    style={{
                      padding: '8px 16px',
                      background: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isDeletingPhoto ? 'wait' : 'pointer',
                      fontSize: '13px',
                      opacity: isDeletingPhoto ? 0.6 : 1
                    }}
                  >
                    {isDeletingPhoto ? 'Deleting...' : 'Delete Photo'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  style={{
                    padding: '10px 16px',
                    background: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: isUploadingPhoto ? 'wait' : 'pointer',
                    fontSize: '14px',
                    opacity: isUploadingPhoto ? 0.6 : 1
                  }}
                >
                  {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#666' }}>
                  JPG, PNG, WebP, or HEIC. Max 10MB.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={handleDeleteTask}
            disabled={isDeleting || isSaving}
            style={{
              padding: '10px 20px',
              background: isDeleting || isSaving ? '#ccc' : '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isDeleting || isSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Task'}
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={isSaving || isDeleting}
              style={{
                padding: '10px 20px',
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isDeleting || !editedTask.description?.trim() || !hasChanges()}
              style={{
                padding: '10px 20px',
                background: (isSaving || isDeleting || !editedTask.description?.trim() || !hasChanges()) ? '#ccc' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: (isSaving || isDeleting || !editedTask.description?.trim() || !hasChanges()) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
